/**
 * The page's flow and state (Sprint 103.0): welcome → (iOS motion step) → the
 * view, with the place, the day summary and the date sheet around it. This file
 * owns the clock: @archisimple/solar reads none (ADR-0091), so "now" is passed
 * in from here.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  advanceTime,
  initialMoment,
  instantToLocal,
  localToInstant,
  sunPosition,
  zoneReading,
  type LocalDate,
  type LocalTime
} from '@archisimple/solar';
import { initialLanguage, translator, type Language } from './i18n/i18n.ts';
import { utcOffset } from './model/format.ts';
import {
  dayAt,
  dayLengthChange,
  hourMarks,
  referencePaths,
  sunAt,
  zoneOf,
  type Place
} from './model/sky.ts';
import { locate } from './sensors/location.ts';
import { motionNeedsPermission, requestMotion } from './sensors/orientation.ts';
import { ArScreen } from './ui/ArScreen.tsx';
import type { ReferenceLine } from './ui/overlay.ts';
import { placeName, WordsContext, type Words } from './ui/common.tsx';
import { DateSheet } from './ui/DateSheet.tsx';
import { LocationScreen } from './ui/LocationScreen.tsx';
import { Summary } from './ui/Summary.tsx';
import { MotionStep, Welcome } from './ui/Welcome.tsx';

type Screen = 'welcome' | 'motion' | 'location' | 'ar' | 'summary';

const LANGUAGE_KEY = 'archisimple-sun.language';
const PLAY_STEP_MINUTES = 10;
const PLAY_INTERVAL_MS = 120;
const LIVE_INTERVAL_MS = 30_000;

function storedLanguage(): string | null {
  try {
    return globalThis.localStorage?.getItem(LANGUAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

function deviceZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/** "21 June", "21 juin": a reference day as its label names it. */
function shortDate(date: LocalDate, language: Language): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Intl.DateTimeFormat(language, {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short'
  }).format(Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1));
}

export function App(): ReactNode {
  const [language, setLanguageState] = useState<Language>(() =>
    initialLanguage(globalThis.navigator?.languages ?? [], storedLanguage())
  );
  const [screen, setScreen] = useState<Screen>('welcome');
  const [place, setPlace] = useState<Place | undefined>(undefined);
  const [locating, setLocating] = useState(false);
  const [locationFailed, setLocationFailed] = useState(false);
  const [wantMotion, setWantMotion] = useState(true);
  const [motionAsked, setMotionAsked] = useState(false);
  const [moment, setMoment] = useState<{ date: LocalDate; time: LocalTime } | undefined>(undefined);
  const [today, setToday] = useState<LocalDate | undefined>(undefined);
  const [live, setLive] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [showReferences, setShowReferences] = useState(false);

  const words: Words = useMemo(
    () => ({
      t: translator(language),
      language,
      setLanguage: (next) => {
        setLanguageState(next);
        try {
          globalThis.localStorage?.setItem(LANGUAGE_KEY, next);
        } catch {
          // A private window may refuse storage; the choice then lasts the visit.
        }
      }
    }),
    [language]
  );
  const { t } = words;

  useEffect(() => {
    document.documentElement.lang = language;
    document.title = `${t('app.name')} · ArchiSimple`;
  }, [language, t]);

  const zone = useMemo(
    () => (place === undefined ? undefined : zoneOf(place, deviceZone())),
    [place]
  );

  /** A place, and the first moment shown there: now, or noon if it is night there. */
  const choosePlace = (chosen: Place): void => {
    const now = Date.now();
    const chosenZone = zoneOf(chosen, deviceZone()).zone;
    const first = initialMoment(now, chosenZone, chosen.latitude, chosen.longitude);
    setPlace(chosen);
    setToday(instantToLocal(now, chosenZone).localDate);
    setMoment({ date: first.localDate, time: first.localTime });
    // At night the first moment is noon, not now: following the clock would undo it.
    setLive(sunPosition(now, chosen.latitude, chosen.longitude).aboveHorizon);
  };

  useEffect(() => {
    if (!live || zone === undefined) return;
    const tick = (): void => {
      const now = instantToLocal(Date.now(), zone.zone);
      setToday(now.localDate);
      setMoment({ date: now.localDate, time: now.localTime });
    };
    tick();
    const timer = setInterval(tick, LIVE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [live, zone]);

  useEffect(() => {
    if (!playing) return;
    const timer = setInterval(() => {
      setMoment((current) =>
        current === undefined
          ? current
          : { ...current, time: advanceTime(current.time, PLAY_STEP_MINUTES) }
      );
    }, PLAY_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [playing]);

  const findPlace = (): void => {
    setLocating(true);
    setLocationFailed(false);
    void locate().then((found) => {
      setLocating(false);
      if (found === undefined) {
        setLocationFailed(true);
        setScreen('location');
      } else {
        choosePlace(found);
      }
    });
  };

  const enterView = (): void => {
    if (motionNeedsPermission() && !motionAsked) {
      setScreen('motion');
    } else {
      setScreen('ar');
    }
  };

  const day = useMemo(
    () => (place && zone && moment ? dayAt(moment.date, zone.zone, place) : undefined),
    [place, zone, moment?.date] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const hours = useMemo(
    () => (place && zone && moment ? hourMarks(moment.date, zone.zone, place) : []),
    [place, zone, moment?.date] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const year = moment === undefined ? undefined : Number(moment.date.slice(0, 4));
  const referenceDays = useMemo(
    () => (place && zone && year !== undefined ? referencePaths(year, zone.zone, place) : []),
    [place, zone, year]
  );
  const references = useMemo(() => referenceDays.map((r) => r.path), [referenceDays]);
  /**
   * The view's reference lines (Sprint 103.1): both solstices always, named as
   * the year's highest and lowest course (June is the lowest in Sydney), and the
   * equinox only when asked for.
   */
  const referenceLines = useMemo((): ReferenceLine[] => {
    const top = (path: readonly { altitudeDegrees: number }[]): number =>
      path.reduce((best, p) => Math.max(best, p.altitudeDegrees), -90);
    const solstices = referenceDays.filter((r) => r.key !== 'march-equinox');
    const highestTop = Math.max(...solstices.map((r) => top(r.path)));
    return referenceDays.flatMap((r): ReferenceLine[] => {
      if (r.key === 'march-equinox') return showReferences ? [{ path: r.path }] : [];
      const above = top(r.path) === highestTop;
      const date = shortDate(r.localDate, language);
      return [
        {
          path: r.path,
          above,
          label: t(above ? 'ar.highest' : 'ar.lowest', { date })
        }
      ];
    });
  }, [referenceDays, showReferences, language, t]);
  const sun =
    place && zone && moment ? sunAt(moment.date, moment.time, zone.zone, place) : undefined;

  const zoneLine = (() => {
    if (zone === undefined || moment === undefined) return '';
    const reading = zoneReading(zone.zone, localToInstant(moment.date, moment.time, zone.zone));
    const offset = utcOffset(reading.utcOffsetMinutes);
    return zone.approximate
      ? t('zone.approximate', { offset })
      : t('zone.named', {
          offset,
          season: t(reading.summerTime ? 'zone.summer' : 'zone.standard')
        });
  })();

  const dateLabel = (date: LocalDate, long: boolean): string => {
    const [y, m, d] = date.split('-').map(Number);
    return new Intl.DateTimeFormat(language, {
      timeZone: 'UTC',
      weekday: long ? 'long' : 'short',
      day: 'numeric',
      month: long ? 'long' : 'short',
      year: 'numeric'
    }).format(Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1));
  };

  let content: ReactNode;
  if (screen === 'welcome') {
    content = (
      <Welcome
        secure={globalThis.isSecureContext !== false}
        onStart={() => {
          findPlace();
          enterView();
        }}
        onManual={() => setScreen('location')}
      />
    );
  } else if (screen === 'motion') {
    content = (
      <MotionStep
        onBack={() => setScreen('welcome')}
        onAllow={() => {
          // Asked inside the tap's handler, as Safari requires.
          void requestMotion().then((granted) => {
            setMotionAsked(true);
            setWantMotion(granted);
            setScreen('ar');
          });
        }}
        onSkip={() => {
          setMotionAsked(true);
          setWantMotion(false);
          setScreen('ar');
        }}
      />
    );
  } else if (screen === 'location') {
    content = (
      <LocationScreen
        initial={place}
        unavailable={locationFailed}
        onBack={() => setScreen(place === undefined ? 'welcome' : 'ar')}
        onRetry={() => {
          findPlace();
          enterView();
        }}
        onUse={(chosen) => {
          choosePlace(chosen);
          setLocationFailed(false);
          enterView();
        }}
      />
    );
  } else if (
    place === undefined ||
    zone === undefined ||
    moment === undefined ||
    day === undefined ||
    sun === undefined
  ) {
    content = (
      <main className="screen" aria-busy={locating}>
        <p className="prose" role="status">
          {t('ar.locating')}
        </p>
      </main>
    );
  } else if (screen === 'summary') {
    content = (
      <Summary
        title={`${placeName(place, t)} · ${dateLabel(moment.date, true)}`}
        day={day}
        references={references}
        sun={sun}
        change={dayLengthChange(moment.date, zone.zone, place)}
        zoneLine={zoneLine}
        onBack={() => setScreen('ar')}
      />
    );
  } else {
    const setTime = (time: LocalTime): void => {
      setLive(false);
      setPlaying(false);
      setMoment({ date: moment.date, time });
    };
    content = (
      <ArScreen
        place={place}
        day={day}
        hours={hours}
        references={referenceLines}
        sun={sun}
        localTime={moment.time}
        dateLabel={dateLabel(moment.date, false)}
        live={live}
        playing={playing}
        wantCamera
        wantMotion={wantMotion}
        onTime={setTime}
        onPlay={() => {
          setLive(false);
          setPlaying((value) => !value);
        }}
        onNow={() => {
          setPlaying(false);
          setLive(true);
        }}
        onOpenDate={() => setSheet(true)}
        onSummary={() => setScreen('summary')}
        onChangePlace={() => setScreen('location')}
        sheet={
          sheet && today !== undefined ? (
            <DateSheet
              place={place}
              zone={zone.zone}
              today={today}
              localDate={moment.date}
              localTime={moment.time}
              day={day}
              playing={playing}
              showReferences={showReferences}
              zoneLine={zoneLine}
              onDate={(date) => {
                setLive(false);
                setMoment({ date, time: moment.time });
              }}
              onTime={setTime}
              onPlay={() => {
                setLive(false);
                setPlaying((value) => !value);
              }}
              onReferences={setShowReferences}
              onClose={() => setSheet(false)}
            />
          ) : null
        }
      />
    );
  }

  return <WordsContext.Provider value={words}>{content}</WordsContext.Provider>;
}
